import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Music2, PlayCircle } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import { API } from "@/lib/api";

export default function Gallery() {
  const { t, language } = useI18n();
  const [songs, setSongs] = useState(null);

  useEffect(() => {
    fetch(`${API}/public/gallery`)
      .then((r) => r.json())
      .then(setSongs)
      .catch(() => setSongs([]));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: t("gallery.title"),
    description: t("gallery.subtitle"),
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={language === "en" ? "Gallery – Real AI Personalized Song Examples | Kalypso Music" : "Galerie – Exemples Réels de Chansons Personnalisées par IA | Kalypso Music"}
        description={language === "en"
          ? "Browse real AI-generated songs created by our community: weddings, birthdays, gifts and more."
          : "Parcourez de vraies chansons générées par IA créées par notre communauté : mariages, anniversaires, cadeaux et plus."}
        path="/galerie"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-medium tracking-tight sm:text-4xl">{t("gallery.title")}</h1>
          <p className="mt-3 text-muted-foreground">{t("gallery.subtitle")}</p>
        </div>

        {songs === null && (
          <div className="mt-16 flex justify-center">
            <Music2 className="h-8 w-8 animate-pulse text-muted-foreground" />
          </div>
        )}

        {songs && songs.length === 0 && (
          <p className="mt-16 text-center text-muted-foreground" data-testid="gallery-empty-state">{t("gallery.empty")}</p>
        )}

        {songs && songs.length > 0 && (
          <div className="mt-14 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {songs.map((song, i) => (
              <motion.div
                key={song.share_token}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 8) * 0.05 }}
              >
                <Link
                  to={`/s/${song.share_token}`}
                  className="group block overflow-hidden rounded-2xl border border-border bg-card"
                  data-testid={`gallery-song-card-${i}`}
                >
                  <div className="relative aspect-square w-full overflow-hidden bg-muted">
                    {song.has_thumbnail ? (
                      <img
                        src={`${API}/public/song/${song.share_token}/thumbnail`}
                        alt={song.title}
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Music2 className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                      <PlayCircle className="h-10 w-10 text-white" />
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="truncate text-sm font-medium">{song.title}</p>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground capitalize">{song.style} · {song.mood}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-16 text-center">
          <Link to="/create">
            <Button size="lg" className="rounded-full px-8">{t("landing.heroCta")}</Button>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
